import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, MapPin, Loader2, ArrowLeft } from "lucide-react";
import type { Profile } from "@/domain/types";
import {
  useAllSeeds,
  useMoneyParts,
  useProject,
  useProgrammeLoading,
  useMilestones,
  useSpendRequests,
  useSeed,
  useActiveProfile,
} from "@/domain/hooks";
import { Stat, MilestoneStepper } from "@/design/components";
import { ImpactLedgerMark } from "@/shell/ImpactLedgerLogo";

/**
 * Stakeholder view (v3 §Chunk 9). A warm, read-only view of a single site: verified
 * progress, money released, and evidence. The human counterpart to the austere operator
 * surfaces. Aggregates only — no contractor-internal budgets or unit prices.
 *
 * Visibility (per product decision): everyone except field reporters. That covers funder,
 * contractor and oversight — the `AudienceRole` set — plus Philanthrify ops (super admin sees
 * everything, §1), in both demo and real-login mode. The route sits behind AuthGuard, so an
 * unauthenticated visitor is sent to /login before reaching here; this in-page check then
 * blocks reporters. NB this narrows the spec's original "public, login-free" intent for the page.
 *
 * Resolves the project through the same workspace-scoped, API-aware hooks the operator
 * surfaces use, so it works when demo mode is off (project ids are numeric DB ids, not seed
 * slugs). The workspace slug is carried in the URL (`/public/:workspaceId/:projectId`); the
 * legacy bare form (`/public/:projectId`) falls back to locating the seed that owns the project.
 */
// Only field reporters are barred from a site's transparency record; every other role
// (funder, contractor, oversight and Philanthrify ops) may view it.
const BLOCKED_VIEWER_GROUPS: ReadonlyArray<Profile["group"]> = ["reporter"];

export function PublicProjectPage() {
  const { workspaceId, projectId = "" } = useParams();
  const navigate = useNavigate();
  const profile = useActiveProfile();
  // navigate(-1) only makes sense when this page was reached via an in-app
  // click (ProjectManagementView's "Stakeholder view" button). This page is
  // also a genuinely shareable public link (see the file doc comment) - the
  // very case it exists for - so it's routinely the FIRST page a visitor
  // loads, with nothing in the SPA's own history to pop back to. react-router
  // v6's history.state.idx is 0 (or absent) for that first entry; -1 there
  // would silently no-op (or worse, exit to whatever was open before this
  // tab), reading as "the back button does nothing." Fall back to a
  // role-appropriate landing instead of assuming history exists.
  const canGoBack = typeof window !== "undefined" && (window.history.state?.idx ?? 0) > 0;
  const fallbackHome = profile.group === "funder" ? "/funder" : profile.group === "oversight" ? "/stratus" : profile.landing;
  const goBack = () => (canGoBack ? navigate(-1) : navigate(fallbackHome));
  const seeds = useAllSeeds();
  const mp = useMoneyParts();
  // The workspace to scope every lookup to. Given in the URL when opened from an operator
  // surface; otherwise derived from seed data for legacy transparency links.
  const wsId = workspaceId ?? seeds.find((s) => s.projects.some((p) => p.id === projectId))?.workspace.id;
  const project = useProject(projectId, wsId);
  const projectLoading = useProgrammeLoading(wsId);
  const milestones = useMilestones(projectId, wsId);
  const asks = useSpendRequests({ projectId }, wsId);
  const seed = useSeed(wsId);

  // Role gate: everyone except field reporters may view a site's record.
  if (BLOCKED_VIEWER_GROUPS.includes(profile.group)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-8 text-center text-mute">
        This page isn’t available for your role.
      </div>
    );
  }

  if (!seed || !project) {
    // The programme detail loads from the API by numeric DB id; until it
    // resolves, the lookup falls back to seed projects (slug ids) and won't
    // find a numeric-id URL. Show a loading state rather than the terminal
    // "not available" screen while that first fetch is still in flight.
    if (projectLoading) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg p-8 text-center text-mute">
          <Loader2 className="h-5 w-5 animate-spin" aria-label="Loading" />
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-8 text-center text-mute">
        This public record is not available.
      </div>
    );
  }

  const disbursed = milestones.filter((m) => m.status === "disbursed" || m.status === "closed").length;
  const pctComplete = milestones.length ? Math.round((disbursed / milestones.length) * 100) : 0;
  const released = seed.disbursements
    .filter((d) => d.state === "completed" && milestones.some((m) => m.id === d.milestoneId))
    .reduce((a, d) => a + d.amount, 0);
  const verifiedCount = asks.filter((r) => r.verifiedById).length;
  const photos = (seed.evidence ?? [])
    .filter((e) => e.type === "photo" && asks.some((r) => r.id === e.linkedAskId))
    .map((e) => e.url)
    .slice(0, 3);
  const gallery = [project.coverImageUrl, ...photos].filter(Boolean).slice(0, 4) as string[];

  return (
    <div className="min-h-screen bg-bg">
      {/* Hero. Scrim uses the same rgb triplet as the --ink-900 design token
          (15,17,41) rather than an ad-hoc approximation, with the top-edge
          floor raised to 0.6 (was 0.25) so the header text stays legible
          over a light/busy cover image. Text uses the ink-scale tokens
          ("sidebar, hero, report dark tiles" per tailwind.config.ts)
          instead of raw white. */}
      <div className="relative">
        <div
          className="h-72 w-full bg-ink"
          style={
            project.coverImageUrl
              ? { backgroundImage: `linear-gradient(to top, rgba(15,17,41,0.9), rgba(15,17,41,0.6)), url(${project.coverImageUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
              : {}
          }
        />
        {/* The operator surfaces navigate here in-app (not a new tab — see
            ProjectManagementView.tsx), so the page owns its own way back. */}
        <div className="absolute inset-x-0 top-0">
          <div className="mx-auto max-w-3xl px-6 pt-6">
            <button
              type="button"
              onClick={goBack}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-inkText1 backdrop-blur-sm transition-colors hover:bg-white/20"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </button>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-3xl px-6 pb-7 text-inkText1">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-xs font-medium backdrop-blur-sm">
              <ShieldCheck className="h-3.5 w-3.5" /> Verified by {seed.workspace.oversight.name}
            </span>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight">{project.name}</h1>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-inkText2">
              <MapPin className="h-4 w-4" /> {project.district} · funded by {seed.workspace.funder.name}
            </p>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-6 py-8">
        <p className="text-base leading-relaxed text-mute">{project.description}</p>

        {/* Progress + money */}
        <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Stat label="Built so far" value={`${pctComplete}%`} sub={`${disbursed} of ${milestones.length} milestones complete`} />
          <Stat label="Funding released" value={mp(released).value} unit={mp(released).unit} sub="against verified progress" />
          <Stat label="Checks passed" value={String(verifiedCount)} sub="spends independently verified" />
        </div>

        <div className="mt-5">
          <div className="mb-1.5 flex items-center justify-between text-xs text-faint">
            <span>Overall progress</span>
            <span className="tabular-nums font-medium text-ink">{pctComplete}% · {disbursed} of {milestones.length} milestones complete</span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-subtle">
            <div className="h-full rounded-full bg-okFill transition-all" style={{ width: `${pctComplete}%` }} />
          </div>
        </div>

        {/* Milestones — the one canonical stepper, vertical for this read-only page */}
        <section className="mt-8">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-faint">Progress, step by step</h2>
          <div className="mt-3">
            <MilestoneStepper milestones={milestones} orientation="vertical" />
          </div>
        </section>

        {/* Evidence */}
        {gallery.length > 0 && (
          <section className="mt-8">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-faint">From the site</h2>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {gallery.map((src, i) => (
                <div key={i} className="aspect-square overflow-hidden rounded-lg border border-line">
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-8 rounded-lg border border-line border-l-4 border-l-ok bg-card p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-ink"><ShieldCheck className="h-4 w-4 text-ok" /> Every figure here is backed by evidence</p>
          <p className="mt-1 text-sm text-mute">
            Funds are only released after {seed.workspace.oversight.name} confirms the work and the spend behind each step. Receipts,
            photographs and reconciliations sit behind every number on this page.
          </p>
        </div>

        <footer className="mt-10 flex items-center justify-center gap-2 border-t border-line pt-6 text-xs text-faint">
          <ImpactLedgerMark size={18} /> Verified on Impact Ledger · powered by Philanthrify
        </footer>
      </main>
    </div>
  );
}
